import { useMutation, useQuery } from "@apollo/client";
import { Alert, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import {
  GATHER_HARMONIZED_VIOLATION_FIELD_NOTES,
  HARMONIZED_VIOLATION_FIELD_NOTES_GATHER_PREVIEW,
} from "hooks/useHarmonizedViolations";
import pluralize from "pluralize";
import { getUserFacingMessage } from "utils/errorUtils";

/** Why some of the selection won't be gathered, or null when all of it will be. */
function leftAloneMessage(
  writtenByPerson: number,
  alreadyGathering: number,
  skipped: number
): string | null {
  const reasons = [
    writtenByPerson > 0 &&
      `${writtenByPerson} ${writtenByPerson === 1 ? "has" : "have"} field notes a person wrote`,
    alreadyGathering > 0 &&
      `${alreadyGathering} ${alreadyGathering === 1 ? "is" : "are"} already being gathered`,
    skipped > 0 && `${skipped} can't be gathered`,
  ].filter((reason): reason is string => Boolean(reason));
  if (reasons.length === 0) return null;
  const total = writtenByPerson + alreadyGathering + skipped;
  const listed =
    reasons.length > 1
      ? `${reasons.slice(0, -1).join(", ")}, and ${reasons.at(-1)}`
      : reasons.join("");
  return `${pluralize("selected violation", total, true)} will be left alone: ${listed}.`;
}

/**
 * Staff bulk gather: warns that it is costly and slow, then lets staff gather
 * everything or skip violations whose notes came from unchanged information.
 */
export function GatherFieldNotesDialog({
  tenantId,
  ids,
  onClose,
  onGathered,
}: {
  tenantId: string;
  ids: string[];
  onClose: () => void;
  onGathered: () => void;
}) {
  const alerts = useAlerts();
  const { data, loading, error } = useQuery(HARMONIZED_VIOLATION_FIELD_NOTES_GATHER_PREVIEW, {
    variables: { tenantId, harmonizedViolationIds: ids },
    fetchPolicy: "network-only",
  });
  const [gather, { loading: gathering }] = useMutation(GATHER_HARMONIZED_VIOLATION_FIELD_NOTES);
  const preview = data?.harmonizedViolationFieldNotesGatherPreview;
  const changed = preview?.changedOrMissing ?? 0;
  const unchanged = preview?.unchanged ?? 0;
  const all = changed + unchanged;

  const run = async (includeUnchanged: boolean) => {
    try {
      const result = await gather({
        variables: { tenantId, harmonizedViolationIds: ids, includeUnchanged },
      });
      const queued = result.data?.gatherHarmonizedViolationFieldNotes ?? 0;
      if (queued === 0) {
        alerts.info("Nothing needed gathering: Scout is already on it or nothing changed.");
      } else {
        alerts.success(
          `Scout is gathering field notes for ${pluralize("violation", queued, true)}.`
        );
        onGathered();
      }
    } catch (e) {
      alerts.error(
        getUserFacingMessage(e) ?? "Could not ask Scout to gather field notes. Try again.",
        e
      );
    } finally {
      onClose();
    }
  };

  const leftAlone = preview
    ? leftAloneMessage(preview.writtenByPerson, preview.alreadyGathering, preview.skipped)
    : null;

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      title="Gather field notes"
      hideCancelButton
      secondaryActionElements={[
        <Button key="cancel" onClick={onClose} disabled={gathering}>
          Cancel
        </Button>,
      ]}
      primaryActionButtonProps={{
        disabled: gathering || all === 0,
        onClick: () => void run(true),
        saveText: unchanged > 0 ? `Gather all (${all})` : `Gather field notes (${all})`,
        startIcon: null,
      }}
      additionalPrimaryActionElements={
        changed > 0 && unchanged > 0
          ? [
              <Button
                key="changed-only"
                variant="outlined"
                disabled={gathering}
                onClick={() => void run(false)}
              >
                {`Only changed or missing (${changed})`}
              </Button>,
            ]
          : []
      }
    >
      <Stack gap={2}>
        <Alert severity="warning">
          Gathering field notes for many violations at once can be costly. Each violation can take
          up to two minutes.
        </Alert>
        {loading ? <CircularProgress size={24} /> : null}
        {error ? (
          <Typography color="error">
            {getUserFacingMessage(error) ?? "Could not check the selection. Try again."}
          </Typography>
        ) : null}
        {unchanged > 0 ? (
          <Typography>
            {`${unchanged} of the ${pluralize("selected violation", ids.length, true)} already ${unchanged === 1 ? "has" : "have"} field notes gathered from unchanged information.`}
          </Typography>
        ) : null}
        {leftAlone ? (
          <Typography variant="body2" color="text.secondary">
            {leftAlone}
          </Typography>
        ) : null}
      </Stack>
    </Dialog>
  );
}
